import { useMemo } from 'react';
import { Box, Skeleton, Typography, useMediaQuery } from '@mui/material';

import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { TimelineOutlined } from '@mui/icons-material';

import { theme } from '../../../../theme';
import { classNames, getResponsiveDimension } from '../../../../utils';
import { LineChart } from '../../../UI/LineChart/LineChart';

const reverseChartColors = {
  text: {
    positive: theme.palette.dashBoar.darkCoral,
    negative: theme.palette.dashBoar.darkmintGreen,
  },
  series: {
    positive: theme.palette.dashBoar.lightCoral,
    negative: theme.palette.dashBoar.mintGreen,
  },
};

export const SessionChart = (props) => {
  const { sudScore, vocScore } = props;
  const { t } = useTranslation();

  const isDesktop = useMediaQuery('(min-width: 1025px)');
  const isLaptop = useMediaQuery('(max-width: 1024px) and (min-width: 768px)');

  const { isLoading: sessionsLoading } = useSelector((state) => state.sessions);

  /**
   * Sets table graph height
   */
  const graphHeight = useMemo(() => {
    if (isDesktop) return getResponsiveDimension(260);
    else if (isLaptop) {
      return getResponsiveDimension(200, 'laptop');
    } else return getResponsiveDimension(160, 'mobile');
  }, [isDesktop, isLaptop]);

  /**
   * Chart options
   */
  const chartOptions = {
    chart: {
      stacked: true,
      type: 'area',
      toolbar: {
        show: false,
      },
      zoom: {
        enabled: false,
      },
    },
    markers: {
      size: 6,
      strokeColors: theme.palette.neutral.grey70,
      strokeWidth: 2,
      strokeOpacity: 0.9,
      fillOpacity: 0.9,
      radius: 2,
      showNullDataPoints: true,
      hover: {
        size: 8,
      },
    },

    tooltip: {
      intersect: false,
      followCursor: true,
      custom({ series, seriesIndex, dataPointIndex, w }) {
        const value = series[seriesIndex][dataPointIndex];
        const delta = value - series[seriesIndex][dataPointIndex - 1];

        let output = '';
        if (!isNaN(delta)) {
          output = ` (${delta > 0 ? '+' : ''}${delta})`;
        }

        return `<div class="tooltip-sparkline"><span class='value'>${value}</span><span class='delta'>${output}</span></div>`;
      },
    },
    title: {},
  };

  /**
   * Sud scores data doesn't exists fallback
   */
  const noSudFallback = useMemo(() => {
    if (!sessionsLoading && !sudScore?.length) {
      return (
        <Box height={graphHeight || '300px'} className='no-data'>
          <TimelineOutlined className='icon' />
          <Typography variant='h3' className='message'>
            {t('errors.graphs.empty-sud')}
          </Typography>
        </Box>
      );
    } else return null;
  }, [graphHeight, sudScore, sessionsLoading, t]);

  /**
   * Voc scores data doesn't exists fallback
   */
  const noVocFallback = useMemo(() => {
    if (!sessionsLoading && !vocScore?.length) {
      return (
        <Box height={graphHeight || '300'} className='no-data'>
          <TimelineOutlined className='icon' />
          <Typography variant='h3' className='message'>
            {t('errors.graphs.empty-voc')}
          </Typography>
        </Box>
      );
    } else return null;
  }, [graphHeight, vocScore, sessionsLoading, t]);

  if (sessionsLoading)
    // if (true)
    return (
      <Box className='charts-container'>
        <Skeleton
          variant='rounded'
          className={classNames('chart loader', {})}
        />
        <Skeleton
          variant='rounded'
          className={classNames('chart loader', {})}
        />
      </Box>
    );

  return (
    <Box className='charts-container'>
      <Box className='chart sud-chart'>
        <Typography variant='h5' className='chart-title'>
          {t('forms.delta_client_last_session')}
        </Typography>
        <LineChart
          customOptions={chartOptions}
          height={graphHeight}
          values={sudScore}
          NoDataFallback={noSudFallback}
        />
      </Box>
      <Box className='chart voc-chart'>
        <Typography variant='h5' className='chart-title'>
          {t('forms.voc_delta_client_last_session')}
        </Typography>
        <LineChart
          customOptions={chartOptions}
          height={graphHeight}
          values={vocScore}
          colors={reverseChartColors}
          NoDataFallback={noVocFallback}
        />
      </Box>
    </Box>
  );
};
